import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import { ACCESS_TREE, isTreePerm, isPlatformTierRole, type AccessNode } from '@mlm/shared';
import {
  addRoleMember,
  createRole,
  deleteRole,
  listPermissions,
  listRoleMembers,
  listRoles,
  removeRoleMember,
  searchUsersForRole,
  setPermissionGrantable,
  setRolePermissions,
  updateRole,
  type PermissionRow,
  type RoleMember,
  type RoleRow,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

/**
 * User Access — the single access-control surface (the per-user Access tab was
 * removed). Three tiers:
 *   • super_admin (us)   — full control; bypasses every check.
 *   • admin (tenant)     — capped: edits only CUSTOM roles, grants only
 *                          tenant-grantable perms, can't touch built-in roles
 *                          or assign super_admin/admin.
 *   • custom staff roles — tenant-defined per-area View/Edit (e.g. Order Packer
 *                          = View Orders; Order Manager = View+Edit Orders).
 *
 * Built-in roles are platform-owned templates: only super_admin edits their
 * perms; tenant admins customize by Cloning. A super_admin-only "Platform" tab
 * tunes the tenant-admin scope (the `admin` role) and the grant cap.
 *
 * Every guard here mirrors the server (apps/api/src/routes/access.ts) — the SPA
 * gating is UX only; the API is the real boundary.
 */
export function AccessPage() {
  const isSuperAdmin = useAuthStore((s) => !!s.user?.roles.includes('super_admin'));

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">User Access</h1>
      </div>

      {isSuperAdmin ? (
        <Tabs defaultValue="roles">
          <TabsList>
            <TabsTrigger value="roles">Roles</TabsTrigger>
            <TabsTrigger value="platform">Platform</TabsTrigger>
          </TabsList>
          <TabsContent value="roles" className="mt-4">
            <RolesManager isSuperAdmin />
          </TabsContent>
          <TabsContent value="platform" className="mt-4">
            <PlatformControls />
          </TabsContent>
        </Tabs>
      ) : (
        <RolesManager isSuperAdmin={false} />
      )}
    </div>
  );
}

type CloneTemplate = { code: string; name: string; description?: string; permissions: string[] };

function RolesManager({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({ queryKey: ['admin-roles'], queryFn: listRoles });
  const permsQuery = useQuery({ queryKey: ['admin-permissions'], queryFn: listPermissions });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // When set, the create form is prefilled from a built-in template and the new
  // role inherits the (cap-filtered) template permissions on create.
  const [cloneTemplate, setCloneTemplate] = useState<CloneTemplate | null>(null);

  const create = useMutation({
    mutationFn: async (input: { code: string; name: string; description?: string }) => {
      const created = await createRole(input);
      if (cloneTemplate?.permissions.length) {
        await setRolePermissions(created.id, cloneTemplate.permissions);
      }
      return created;
    },
    meta: { successMessage: 'Role created.' },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      setSelectedId(r.id);
      setCreating(false);
      setCloneTemplate(null);
    },
  });

  const selected = rolesQuery.data?.find((r) => r.id === selectedId) ?? null;
  const permByCode = useMemo(
    () => new Map((permsQuery.data ?? []).map((p) => [p.code, p])),
    [permsQuery.data],
  );

  function startClone(role: RoleRow) {
    // Tenant admins can only carry over grantable perms; super_admin carries all.
    const perms = role.permissions.filter((c) => isSuperAdmin || permByCode.get(c)?.grantable);
    setCloneTemplate({
      code: `${role.code}_copy`,
      name: `${role.name} (copy)`,
      description: role.description ?? undefined,
      permissions: perms,
    });
    setCreating(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <p className="text-sm text-muted-foreground max-w-3xl">
          Define roles and choose what each can view and edit, area by area. Built-in roles are
          platform templates — {isSuperAdmin ? 'edit them here, or' : 'you can'} clone one to make
          an editable custom role. Changes apply on the user's next login (permissions are baked
          into their session).
        </p>
        <Button
          onClick={() => {
            setCloneTemplate(null);
            setCreating(true);
          }}
        >
          + New role
        </Button>
      </div>

      {creating && (
        <RoleCreateForm
          submitting={create.isPending}
          initial={cloneTemplate ?? undefined}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => {
            setCreating(false);
            setCloneTemplate(null);
          }}
          error={create.error}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Roles</CardTitle>
          </CardHeader>
          <CardContent>
            {rolesQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (rolesQuery.data?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">No roles yet.</p>
            ) : (
              <ul className="space-y-1">
                {rolesQuery.data!.map((r) => (
                  <li key={r.id}>
                    <button
                      onClick={() => setSelectedId(r.id)}
                      className={`w-full text-left p-2 rounded-md border text-sm ${
                        selectedId === r.id ? 'border-primary bg-accent/40' : 'hover:bg-accent'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{r.name}</div>
                          <div className="text-xs font-mono text-muted-foreground truncate">
                            {r.code}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {isPlatformTierRole(r.code) ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                              platform
                            </span>
                          ) : r.builtin ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              built-in
                            </span>
                          ) : null}
                          <span className="text-[10px] text-muted-foreground">
                            {r.userCount} user{r.userCount === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          {selected ? (
            <RoleDetail
              role={selected}
              permissions={permsQuery.data ?? []}
              isSuperAdmin={isSuperAdmin}
              onChanged={() => queryClient.invalidateQueries({ queryKey: ['admin-roles'] })}
              onClone={() => startClone(selected)}
            />
          ) : (
            <Card>
              <CardContent className="text-sm text-muted-foreground py-12 text-center">
                Select a role to view or edit its access.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function RoleCreateForm({
  submitting,
  initial,
  onSubmit,
  onCancel,
  error,
}: {
  submitting: boolean;
  initial?: { code: string; name: string; description?: string };
  onSubmit: (input: { code: string; name: string; description?: string }) => void;
  onCancel: () => void;
  error: unknown;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const errMsg = (error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;

  return (
    <Card className="bg-muted/30">
      <CardHeader>
        <CardTitle>{initial ? 'Clone role' : 'New role'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({ code, name, description: description || undefined });
          }}
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="role-code">
              Code
            </label>
            <Input
              id="role-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              placeholder="order_packer, accountant, …"
              required
            />
            <p className="text-xs text-muted-foreground">
              Lowercase, letters / digits / underscore. Used by code to gate routes.
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="role-name">
              Name
            </label>
            <Input id="role-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="md:col-span-2 space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="role-desc">
              Description
            </label>
            <Input
              id="role-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this role is for"
            />
          </div>
          {initial && (
            <p className="md:col-span-2 text-xs text-muted-foreground">
              The new role starts with a copy of this template's permissions (within what you're
              allowed to grant). Adjust them after creating.
            </p>
          )}
          {errMsg && (
            <p className="md:col-span-2 text-sm text-destructive">{prettyError(errMsg)}</p>
          )}
          <div className="md:col-span-2 flex gap-2 pt-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : initial ? 'Create clone' : 'Create role'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function RoleDetail({
  role,
  permissions,
  isSuperAdmin,
  onChanged,
  onClone,
}: {
  role: RoleRow;
  permissions: PermissionRow[];
  isSuperAdmin: boolean;
  onChanged: () => void;
  onClone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');
  const [picked, setPicked] = useState<Set<string>>(new Set(role.permissions));
  const [deleting, setDeleting] = useState(false);
  const [deleteSubmitError, setDeleteSubmitError] = useState<string | null>(null);
  useEffect(() => {
    setName(role.name);
    setDescription(role.description ?? '');
    setPicked(new Set(role.permissions));
  }, [role.id, role.name, role.description, role.permissions]);

  const permByCode = useMemo(() => new Map(permissions.map((p) => [p.code, p])), [permissions]);

  // Who may edit this role's permissions? super_admin: any role except the
  // fully-locked super_admin role. Tenant admin: custom roles only. Built-in
  // roles are platform-owned templates (clone to customize).
  const canEditPerms = !role.permissionsLocked && (isSuperAdmin || !role.builtin);
  // Tenant admins can't assign platform-tier roles to users.
  const canManageMembers = isSuperAdmin || !isPlatformTierRole(role.code);

  const dirtyMeta = name !== role.name || (description || null) !== (role.description ?? null);
  const dirtyPerms = useMemo(() => {
    const current = new Set(role.permissions);
    if (current.size !== picked.size) return true;
    for (const c of picked) if (!current.has(c)) return true;
    return false;
  }, [picked, role.permissions]);

  const renameMu = useMutation({
    mutationFn: () => updateRole(role.id, { name, description: description || null }),
    meta: { successMessage: 'Role updated.' },
    onSuccess: () => onChanged(),
  });
  const permsMu = useMutation({
    mutationFn: () => setRolePermissions(role.id, Array.from(picked)),
    meta: { successMessage: 'Access saved.' },
    onSuccess: () => onChanged(),
  });
  const deleteMu = useMutation({
    mutationFn: () => deleteRole(role.id),
    meta: { successMessage: 'Role deleted.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-roles'] }),
  });

  const renameError = (renameMu.error as { response?: { data?: { error?: string } } } | null)
    ?.response?.data?.error;
  const permsError = (
    permsMu.error as {
      response?: { data?: { error?: string; missing?: string[]; blocked?: string[] } };
    } | null
  )?.response?.data;
  const deleteError = (deleteMu.error as { response?: { data?: { error?: string } } } | null)
    ?.response?.data?.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <span>{role.name}</span>
          <span className="text-sm font-mono font-normal text-muted-foreground">{role.code}</span>
          {isPlatformTierRole(role.code) ? (
            <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">
              platform
            </span>
          ) : role.builtin ? (
            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              built-in
            </span>
          ) : null}
          <div className="ml-auto">
            <Button size="sm" variant="outline" onClick={onClone}>
              Clone as custom role
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Rename (custom roles only) */}
        <div className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={role.builtin}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Description</label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={role.builtin}
              />
            </div>
          </div>
          {!role.builtin && (
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!dirtyMeta || renameMu.isPending}
                onClick={() => renameMu.mutate()}
              >
                {renameMu.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={role.userCount > 0 || deleteMu.isPending}
                onClick={() => {
                  setDeleteSubmitError(null);
                  setDeleting(true);
                }}
              >
                {deleteMu.isPending ? 'Deleting…' : 'Delete role'}
              </Button>
            </div>
          )}
          <ConfirmDialog
            open={deleting}
            onOpenChange={(next) => {
              if (!next) {
                setDeleting(false);
                setDeleteSubmitError(null);
              }
            }}
            title={`Delete role "${role.name}"`}
            description={
              <span>
                Removes the role from the registry. Users currently assigned this role must be
                unassigned first. Cannot be undone.
              </span>
            }
            confirmLabel="Delete role"
            confirmVariant="destructive"
            loading={deleteMu.isPending}
            errorMessage={deleteSubmitError}
            typedConfirm={{ expected: role.name }}
            onConfirm={async () => {
              setDeleteSubmitError(null);
              try {
                await deleteMu.mutateAsync();
                setDeleting(false);
              } catch (err) {
                setDeleteSubmitError(err instanceof Error ? err.message : 'Failed to delete role.');
              }
            }}
          />
          {renameError && <p className="text-xs text-destructive">{prettyError(renameError)}</p>}
          {deleteError && <p className="text-xs text-destructive">{prettyError(deleteError)}</p>}
          {role.userCount > 0 && !role.builtin && (
            <p className="text-xs text-muted-foreground">
              {role.userCount} user{role.userCount === 1 ? '' : 's'} have this role. Unassign them
              before deleting.
            </p>
          )}
        </div>

        {/* Access tree */}
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="text-sm font-medium">What this role can access</h3>
            {role.permissionsLocked ? (
              <span className="text-xs text-muted-foreground">
                super_admin bypasses every check — permissions here have no effect.
              </span>
            ) : !canEditPerms ? (
              <span className="text-xs text-muted-foreground">
                Built-in template — clone it to make an editable role.
              </span>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Enable a page to let this role open it (and load its data); turn on individual actions
            to let it do things. Changes take effect on the user's next sign-in.
          </p>
          <PermissionTree
            picked={picked}
            setPicked={setPicked}
            permByCode={permByCode}
            isSuperAdmin={isSuperAdmin}
            readOnly={!canEditPerms}
          />
          <AdvancedPermissions
            permissions={permissions}
            picked={picked}
            setPicked={setPicked}
            isSuperAdmin={isSuperAdmin}
            readOnly={!canEditPerms}
          />
          {permsError?.missing && (
            <p className="text-xs text-destructive">
              Unknown permission codes: {permsError.missing.join(', ')}
            </p>
          )}
          {permsError?.blocked && (
            <p className="text-xs text-destructive">
              You can't grant these permissions: {permsError.blocked.join(', ')}
            </p>
          )}
          {permsError && !permsError.missing && !permsError.blocked && (
            <p className="text-xs text-destructive">{prettyError(permsError.error ?? '')}</p>
          )}
          {canEditPerms && (
            <Button
              size="sm"
              disabled={!dirtyPerms || permsMu.isPending}
              onClick={() => permsMu.mutate()}
            >
              {permsMu.isPending ? 'Saving…' : 'Save access'}
            </Button>
          )}
        </div>

        {/* Assigned members */}
        <AssignedMembers role={role} canManage={canManageMembers} />
      </CardContent>
    </Card>
  );
}

/**
 * Collapsible access tree mirroring the admin UI: Section → Page → tab/button.
 * Toggling a PAGE grants its nav perm + the read perm(s) it needs to load
 * (`requires[]`); toggling a child action grants that perm and implies the page.
 * Informational children (shared authority) render as a label, not a toggle.
 */
function PermissionTree({
  picked,
  setPicked,
  permByCode,
  isSuperAdmin,
  readOnly,
}: {
  picked: Set<string>;
  setPicked: Dispatch<SetStateAction<Set<string>>>;
  permByCode: Map<string, PermissionRow>;
  isSuperAdmin: boolean;
  readOnly: boolean;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function setPerms(mutate: (next: Set<string>) => void) {
    setPicked((prev) => {
      const next = new Set(prev);
      mutate(next);
      return next;
    });
  }

  // A perm is grantable to this editor if it exists and (super_admin OR flagged
  // grantable). Unknown codes (not in the catalog) are treated as un-grantable.
  const grantableP = (code: string) => isSuperAdmin || !!permByCode.get(code)?.grantable;

  // Page node: its perm + every requires[]; grantable only if ALL are grantable.
  const pageView = (pageNode: AccessNode) => [pageNode.perm!, ...(pageNode.requires ?? [])];
  const lockableChildren = (pageNode: AccessNode) =>
    (pageNode.children ?? []).filter((c) => c.lockable && c.perm);

  return (
    <div className="rounded-md border divide-y">
      {ACCESS_TREE.map((sec) => (
        <div key={sec.key} className="px-3 py-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
            {sec.label}
          </div>
          <ul className="space-y-1">
            {(sec.children ?? []).map((pg) => {
              const views = pageView(pg);
              const pageOn = !!pg.perm && picked.has(pg.perm);
              const pageGrantable = views.every(grantableP);
              const children = pg.children ?? [];
              const hasToggleChildren = children.length > 0;
              const isOpen = expanded.has(pg.key);

              return (
                <li key={pg.key}>
                  <div className="flex items-center gap-2">
                    {hasToggleChildren ? (
                      <button
                        type="button"
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                        className="text-muted-foreground hover:text-foreground text-xs w-4"
                        onClick={() =>
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(pg.key)) next.delete(pg.key);
                            else next.add(pg.key);
                            return next;
                          })
                        }
                      >
                        {isOpen ? '▾' : '▸'}
                      </button>
                    ) : (
                      <span className="w-4" />
                    )}
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={pageOn}
                        disabled={readOnly || !pageGrantable}
                        onCheckedChange={() =>
                          setPerms((next) => {
                            if (pageOn) {
                              // Disabling a page removes its view perms + all of
                              // its action children.
                              views.forEach((v) => next.delete(v));
                              lockableChildren(pg).forEach((c) => next.delete(c.perm!));
                            } else {
                              views.forEach((v) => next.add(v));
                            }
                          })
                        }
                      />
                      {pg.label}
                    </label>
                  </div>

                  {hasToggleChildren && isOpen && (
                    <ul className="ml-6 mt-1 mb-1 border-l border-border pl-3 space-y-1">
                      {children.map((child) => {
                        if (!child.lockable || !child.perm) {
                          // Informational (shares the page's authority).
                          return (
                            <li
                              key={child.key}
                              className="text-xs text-muted-foreground/70 flex items-center gap-1.5"
                            >
                              <span className="w-3.5 text-center">·</span>
                              {child.label}{' '}
                              <span className="text-muted-foreground/50">(included)</span>
                            </li>
                          );
                        }
                        const childPerm = child.perm;
                        const on = picked.has(childPerm);
                        const disabled = readOnly || !grantableP(childPerm);
                        const isBtn = child.kind === 'button';
                        return (
                          <li key={child.key}>
                            <label
                              className={`flex items-center gap-2 text-xs ${
                                disabled ? 'opacity-60' : 'cursor-pointer'
                              }`}
                            >
                              <Checkbox
                                checked={on}
                                disabled={disabled}
                                onCheckedChange={() =>
                                  setPerms((next) => {
                                    if (on) {
                                      next.delete(childPerm);
                                    } else {
                                      // Enabling an action implies the page can load.
                                      views.forEach((v) => next.add(v));
                                      next.add(childPerm);
                                    }
                                  })
                                }
                              />
                              <span className="text-muted-foreground">{isBtn ? 'do' : 'view'}</span>
                              {child.label}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * Raw permission picker for codes NOT surfaced by the tree (POS, audit,
 * announcements, etc.). Tenant admins only see grantable codes.
 */
function AdvancedPermissions({
  permissions,
  picked,
  setPicked,
  isSuperAdmin,
  readOnly,
}: {
  permissions: PermissionRow[];
  picked: Set<string>;
  setPicked: Dispatch<SetStateAction<Set<string>>>;
  isSuperAdmin: boolean;
  readOnly: boolean;
}) {
  const [open, setOpen] = useState(false);

  const grouped = useMemo(() => {
    const extra = permissions.filter((p) => !isTreePerm(p.code) && (isSuperAdmin || p.grantable));
    const map = new Map<string, PermissionRow[]>();
    for (const p of extra) {
      const group = p.code.includes('.') ? p.code.split('.')[0]! : '_other';
      const list = map.get(group) ?? [];
      list.push(p);
      map.set(group, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permissions, isSuperAdmin]);

  if (grouped.length === 0) return null;

  function toggle(code: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <span>Advanced (raw permissions)</span>
        <span className="text-xs">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 max-h-80 overflow-auto">
          {grouped.map(([group, perms]) => (
            <div key={group}>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                {group === '_other' ? 'other' : group}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {perms.map((p) => (
                  <label
                    key={p.code}
                    className={`flex items-start gap-2 text-sm ${
                      readOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <Checkbox
                      checked={picked.has(p.code)}
                      disabled={readOnly}
                      onCheckedChange={() => toggle(p.code)}
                      className="mt-1"
                    />
                    <div className="min-w-0">
                      <div className="font-mono text-xs">{p.code}</div>
                      {p.description && (
                        <div className="text-xs text-muted-foreground">{p.description}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Role-centric assignment — replaces the removed per-user Access tab. */
function AssignedMembers({ role, canManage }: { role: RoleRow; canManage: boolean }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const membersQuery = useQuery({
    queryKey: ['role-members', role.id],
    queryFn: () => listRoleMembers(role.id),
  });

  const results = useQuery({
    queryKey: ['role-member-search', search],
    queryFn: () => searchUsersForRole(search),
    enabled: canManage && search.trim().length >= 2,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['role-members', role.id] });
    queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
  };

  const add = useMutation({
    mutationFn: (userId: string) => addRoleMember(role.id, userId),
    meta: { successMessage: 'User added to role.' },
    onSuccess: () => {
      setSearch('');
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => removeRoleMember(role.id, userId),
    meta: { successMessage: 'User removed from role.' },
    onSuccess: invalidate,
  });

  const memberIds = new Set((membersQuery.data ?? []).map((m) => m.id));
  const addError = (add.error as { response?: { data?: { error?: string } } } | null)?.response
    ?.data?.error;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Assigned members</h3>
      {!canManage && (
        <p className="text-xs text-muted-foreground">Only a super admin can assign this role.</p>
      )}

      {canManage && (
        <div className="space-y-1.5">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search a user by name or email to add…"
          />
          {search.trim().length >= 2 && (
            <div className="rounded-md border divide-y max-h-48 overflow-auto">
              {results.isLoading ? (
                <p className="p-2 text-xs text-muted-foreground">Searching…</p>
              ) : (results.data?.length ?? 0) === 0 ? (
                <p className="p-2 text-xs text-muted-foreground">No matches.</p>
              ) : (
                results.data!.map((u) => (
                  <button
                    key={u.id}
                    disabled={memberIds.has(u.id) || add.isPending}
                    onClick={() => add.mutate(u.id)}
                    className="w-full text-left p-2 text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
                  >
                    <span className="min-w-0 truncate">
                      {u.fullName ?? u.email}{' '}
                      <span className="text-xs text-muted-foreground">{u.email}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {memberIds.has(u.id) ? 'Already assigned' : 'Add'}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
          {addError && <p className="text-xs text-destructive">{prettyError(addError)}</p>}
        </div>
      )}

      <div className="rounded-md border divide-y">
        {membersQuery.isLoading ? (
          <p className="p-2 text-xs text-muted-foreground">Loading…</p>
        ) : (membersQuery.data?.length ?? 0) === 0 ? (
          <p className="p-2 text-xs text-muted-foreground">No users have this role.</p>
        ) : (
          membersQuery.data!.map((m: RoleMember) => (
            <div key={m.id} className="flex items-center justify-between gap-2 p-2 text-sm">
              <span className="min-w-0 truncate">
                {m.fullName ?? m.email}{' '}
                <span className="text-xs text-muted-foreground">{m.email}</span>
              </span>
              {canManage && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(m.id)}
                >
                  Remove
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * super_admin-only Platform tab. "What tenant admins can grant" — flip each
 * permission's grantable flag. (Editing the `admin` role's own scope happens on
 * the Roles tab, where super_admin can now edit built-in roles.)
 */
function PlatformControls() {
  const queryClient = useQueryClient();
  const permsQuery = useQuery({ queryKey: ['admin-permissions'], queryFn: listPermissions });

  const toggle = useMutation({
    mutationFn: ({ code, grantable }: { code: string; grantable: boolean }) =>
      setPermissionGrantable(code, grantable),
    meta: { successMessage: 'Grant cap updated.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-permissions'] }),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, PermissionRow[]>();
    for (const p of permsQuery.data ?? []) {
      const group = p.code.includes('.') ? p.code.split('.')[0]! : '_other';
      const list = map.get(group) ?? [];
      list.push(p);
      map.set(group, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permsQuery.data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tenant-admin grant cap</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground max-w-3xl">
          Choose which permissions a tenant admin may grant to the roles they create. Unchecked
          permissions stay super-admin-only (a tenant admin can neither grant them nor see them in
          the role builder). You bypass this cap entirely. To set what a tenant admin can see/edit
          themselves, edit the <span className="font-mono">admin</span> role on the Roles tab.
        </p>
        {permsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-3 max-h-[28rem] overflow-auto p-1">
            {grouped.map(([group, perms]) => (
              <div key={group}>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  {group === '_other' ? 'other' : group}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {perms.map((p) => (
                    <label key={p.code} className="flex items-start gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={p.grantable}
                        disabled={toggle.isPending}
                        onCheckedChange={() =>
                          toggle.mutate({ code: p.code, grantable: !p.grantable })
                        }
                        className="mt-1"
                      />
                      <div className="min-w-0">
                        <div className="font-mono text-xs">{p.code}</div>
                        {p.description && (
                          <div className="text-xs text-muted-foreground">{p.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function prettyError(code: string): string {
  switch (code) {
    case 'reserved_role_code':
      return 'That code is reserved for a built-in role.';
    case 'role_code_taken':
      return 'A role with that code already exists.';
    case 'builtin_role_locked':
      return 'Built-in roles are platform-owned — clone one to customize.';
    case 'super_admin_permissions_locked':
      return 'super_admin permissions are fixed.';
    case 'permission_not_grantable':
      return "You can't grant one or more of those permissions.";
    case 'platform_role_assign_forbidden':
      return 'Only a super admin can assign this role.';
    case 'role_in_use':
      return 'Cannot delete a role assigned to users.';
    case 'cashier_member_mutually_exclusive':
    case 'manager_member_mutually_exclusive':
      return 'POS staff (cashier/manager) and member roles cannot be combined on one user.';
    default:
      return code;
  }
}
